import { z } from "zod";

/**
 * In-browser (WebRTC) calling — Phase 15. Replaces the old Phase 12
 * click-to-call bridge (ring Chris's phone first, then <Dial> the
 * contact) with a real Twilio Voice SDK `Device` running in the browser
 * tab, so the call audio goes through whatever mic/speaker the browser
 * has (Chris's Android Chrome, most of the time).
 *
 * GET /api/v2/voice/token -> a Twilio Access Token scoped to a Voice
 * grant, used to construct a `Device` client-side. `enabled: false` +
 * `token: null` is the honest "Twilio isn't configured on the server
 * yet" state — the frontend must not treat a null token as an error to
 * retry, just as "in-app calling isn't set up."
 */
export const VoiceTokenSchema = z.object({
  token: z.string().nullable(),
  identity: z.string().nullable(),
  enabled: z.boolean(),
  ttl: z.number().nullable(),
});
export type VoiceToken = z.infer<typeof VoiceTokenSchema>;

/**
 * POST /api/v2/contacts/{id}/call/log {status?, duration_sec?} — logs a
 * browser call as a call-kind Interaction once it ends, so it slots into
 * the same History timeline as every other logged touch (Phase 10/12
 * pattern). Best-effort: a failure to log should never re-surface as a
 * call failure to the user, since the call itself already happened.
 */
export const CallLogStatusSchema = z.enum([
  "completed",
  "no_answer",
  "busy",
  "failed",
  "canceled",
]);
export type CallLogStatus = z.infer<typeof CallLogStatusSchema>;

export interface LogCallInput {
  contactId: string;
  status?: CallLogStatus;
  duration_sec?: number;
}
